import React, { useEffect, useMemo, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import { AppButton } from './AppButton';
import { AppInput } from './AppInput';
import { AppTimeField } from './AppTimeField';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import {
  isWebPlatform,
  webModalCard,
  webModalOverlay,
} from '../utils/webLayout';
import { isCompactWidth, overlayPaddingForWidth } from '../utils/responsiveLayout';
import {
  fillBreakTimes,
  formatDurationClock,
  parseClockToMinutes,
  parseDurationToMinutes,
  suggestedOpenBreakEndClock,
  timestampToClock,
  validateCheckoutBreak,
} from '../utils/checkoutBreak';

export type CheckoutBreakValues = {
  startClock: string;
  endClock: string;
  durationMinutes: number | null;
  /** False when the employee checked out without taking a break. */
  recordBreak: boolean;
  recordBreakStart: boolean;
};

type Props = {
  visible: boolean;
  mode: 'missing' | 'open';
  checkInClock?: string | null;
  checkOutClock?: string | null;
  initialStart?: string | null;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (values: CheckoutBreakValues) => void;
};

export function CheckoutBreakModal({
  visible,
  mode,
  checkInClock,
  checkOutClock,
  initialStart,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  const palette = useAppColors();
  const { width } = useWindowDimensions();
  const asDialog = isWebPlatform() || !isCompactWidth(width);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [durationText, setDurationText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!visible) return;
    const startClock = timestampToClock(initialStart) ?? '';
    const suggestedEnd =
      mode === 'open'
        ? suggestedOpenBreakEndClock(
            initialStart ?? startClock,
            checkOutClock ?? '',
          ) ?? ''
        : '';
    const filled = fillBreakTimes({
      start: startClock || null,
      end: suggestedEnd || null,
      durationMinutes: null,
      changed: 'end',
      fallbackEndClock: checkOutClock,
    });
    setStart(filled.start ?? startClock);
    setEnd(filled.end ?? suggestedEnd);
    setDurationText(
      filled.durationMinutes != null
        ? formatDurationClock(filled.durationMinutes)
        : '',
    );
    setError('');
  }, [visible, mode, initialStart, checkOutClock]);

  const themed = useMemo(
    () => ({
      title: { ...typography.sectionTitle, color: palette.textPrimary },
      body: { ...typography.body, color: palette.textSecondary },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );

  function applyChange(
    nextStart: string,
    nextEnd: string,
    nextDurationText: string,
    changed: 'start' | 'end' | 'duration',
  ) {
    const filled = fillBreakTimes({
      start: nextStart || null,
      end: nextEnd || null,
      durationMinutes: parseDurationToMinutes(nextDurationText),
      changed,
      fallbackEndClock: checkOutClock,
    });
    setStart(filled.start ?? '');
    setEnd(filled.end ?? '');
    setDurationText(
      filled.durationMinutes != null
        ? formatDurationClock(filled.durationMinutes)
        : nextDurationText,
    );
    setError('');
  }

  const recordingBreak = mode === 'open' || Boolean(start.trim() && end.trim());

  function confirm() {
    const durationMinutes = parseDurationToMinutes(durationText);
    const message = validateCheckoutBreak({
      start: start || null,
      end: end || null,
      durationMinutes,
      checkInClock,
      checkOutClock,
      requireTimes: mode === 'open',
    });
    if (message) {
      setError(message);
      return;
    }
    const recordBreak = Boolean(start.trim() && end.trim());
    const recordedDuration =
      recordBreak && durationMinutes == null
        ? (parseClockToMinutes(end) ?? 0) - (parseClockToMinutes(start) ?? 0)
        : durationMinutes;
    onConfirm({
      startClock: recordBreak ? start : '',
      endClock: recordBreak ? end : '',
      durationMinutes: recordBreak ? recordedDuration : null,
      recordBreak,
      recordBreakStart: mode === 'missing' && recordBreak,
    });
  }

  if (!visible) return null;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <KeyboardAvoidingView
        style={[
          styles.overlay,
          asDialog ? [webModalOverlay, { padding: overlayPaddingForWidth(width) }] : null,
        ]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <Pressable style={styles.backdrop} onPress={busy ? undefined : onCancel} />
        <View
          style={[
            styles.sheet,
            asDialog ? webModalCard : null,
            { backgroundColor: palette.surface, borderColor: palette.border },
          ]}
        >
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={themed.title}>
              {mode === 'open' ? 'Confirm Break End' : 'Record Your Break'}
            </Text>
            <Text style={themed.body}>
              {mode === 'open'
                ? 'Your break was started but the end time is missing. Enter or confirm when the break ended. Open breaks are automatically closed after 1 hour if you forget to end them.'
                : 'Break start and end are optional. Leave both blank if you did not take a break. If you took a break, enter the actual start and end times. Duration is calculated from those times.'}
            </Text>
            <View style={styles.fields}>
              <AppTimeField
                label="Break start time"
                value={start}
                onChange={value => applyChange(value, end, durationText, 'start')}
                editable={
                  (mode === 'missing' || !timestampToClock(initialStart)) &&
                  !busy
                }
              />
              <AppTimeField
                label="Break end time"
                value={end}
                onChange={value => applyChange(start, value, durationText, 'end')}
                editable={!busy}
              />
              <AppInput
                label="Total break duration"
                value={durationText}
                onChangeText={value => applyChange(start, end, value, 'duration')}
                placeholder="1:00"
                keyboardType="numbers-and-punctuation"
                editable={!busy}
              />
            </View>
            {error ? (
              <Text accessibilityRole="alert" style={themed.error}>
                {error}
              </Text>
            ) : null}
            <View style={styles.actions}>
              <AppButton
                title="Cancel"
                variant="secondary"
                onPress={onCancel}
                disabled={busy}
              />
              <AppButton
                title={
                  busy
                    ? 'Saving…'
                    : recordingBreak
                      ? 'Save and check out'
                      : 'Check out without a break'
                }
                onPress={confirm}
                disabled={busy}
                loading={busy}
              />
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  sheet: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    borderWidth: 1,
    padding: spacing.lg,
    maxHeight: '88%',
    gap: spacing.md,
  },
  fields: { gap: spacing.md, marginTop: spacing.md },
  actions: { gap: spacing.sm, marginTop: spacing.lg, width: '100%' },
});
